import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from "react";

import {
  CloseOutlined,
  DownloadOutlined,
  FileTextOutlined,
  LoadingOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { Button, Modal } from "antd";
import axios from "axios";

import PdfPreview from "../PdfPreview";
import StyledTooltip from "./StyledTooltip";

import "./PdfPreviewModal.css";

const isCanceled = (error) => {
  if (!error) return false;
  if (typeof axios.isCancel === "function" && axios.isCancel(error)) return true;
  return error.name === "CanceledError" || error.code === "ERR_CANCELED";
};

const isBlob = (value) => value instanceof Blob;

const isObjectUrl = (value) => (
  typeof value === "string" && /^(blob:|data:)/i.test(value)
);

const isRemoteUrl = (value) => (
  typeof value === "string" && /^(https?:)?\/\//i.test(value)
);

const fetchPdfBlob = async (url, signal) => {
  const response = await axios.get(url, {
    responseType: "blob",
    headers: { "Cache-Control": "no-cache" },
    params: { disableInterceptor: true },
    ...(signal && { signal }),
  });

  return new Blob([response.data], { type: "application/pdf" });
};

const resolveSource = async (file, signal) => {
  let source = file?.src;

  if (typeof file?.getSrc === "function") {
    source = await file.getSrc(signal);
  }

  return source;
};

const downloadFromUrl = (url, fileName) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

const getModalWidth = (width) => {
  const preferred = typeof width === "number" ? `${width}px` : width;
  return `min(max(${preferred}, min(640px, calc(100vw - 32px))), calc(100vw - 32px))`;
};

const printFromUrl = (url) => {
  const printWindow = window.open(url, "_blank", "noopener,noreferrer");
  if (!printWindow) return;

  const triggerPrint = () => {
    try {
      printWindow.focus();
      printWindow.print();
    } catch (error) {
      // Cross-origin PDFs may block print(); the opened tab can still be used.
    }
  };

  printWindow.addEventListener("load", triggerPrint);
  setTimeout(triggerPrint, 750);
};

export default function PdfPreviewModal({
  open = false,
  onClose,
  files = [],
  activeKey,
  onActiveKeyChange,
  title,
  showDownload = true,
  showPrint = true,
  downloadLabel,
  downloadLoading = false,
  errorMessage = "Unable to preview this PDF.",
  className = "",
  width = 920,
  zIndex,
  loading: externalLoading = false,
  downloadDisabled = false,
  onDownload,
  extra,
  headerExtra,
  footer,
  maskClosable = true,
}) {
  const [internalActiveKey, setInternalActiveKey] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState(false);
  const [viewerFailed, setViewerFailed] = useState(false);
  const [documentReady, setDocumentReady] = useState(false);
  const previewUrlRef = useRef("");
  const shouldRevokeRef = useRef(false);
  const blobSrcRef = useRef(null);
  const filesRef = useRef(files);

  filesRef.current = files;

  const fileKeys = files.map((file) => file.key).join("|");
  const firstFileKey = files[0]?.key || "";
  const resolvedActiveKey = activeKey || internalActiveKey || firstFileKey;
  const activeFile = useMemo(
    () => files.find((file) => file.key === resolvedActiveKey) || files[0] || null,
    [files, resolvedActiveKey],
  );
  const activeFileSrc = activeFile?.src;
  const showTabs = !title && files.some((file) => file.label);
  const showLabeledDownload = Boolean(downloadLabel);
  const canUsePreview = Boolean(previewUrl) && documentReady && !error && !viewerFailed;
  const isPending = open && !error && !viewerFailed && !documentReady;
  const canTriggerDownload = typeof onDownload === "function"
    ? !downloadDisabled
    : canUsePreview;

  const revokePreviewUrl = useCallback(() => {
    if (shouldRevokeRef.current && previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
    previewUrlRef.current = "";
    shouldRevokeRef.current = false;
    blobSrcRef.current = null;
    setPreviewUrl("");
    setDocumentReady(false);
    setViewerFailed(false);
  }, []);

  const showPreview = useCallback((url, { revoke = false, blob = null } = {}) => {
    if (!url) return;
    if (previewUrlRef.current === url && blobSrcRef.current === blob) {
      setError(false);
      return;
    }

    const previousUrl = previewUrlRef.current;
    const shouldRevokePrevious = shouldRevokeRef.current;

    previewUrlRef.current = url;
    shouldRevokeRef.current = revoke;
    blobSrcRef.current = blob;
    setPreviewUrl(url);
    setDocumentReady(false);
    setError(false);

    if (shouldRevokePrevious && previousUrl && previousUrl !== url) {
      URL.revokeObjectURL(previousUrl);
    }
  }, []);

  const setActiveFileKey = (nextKey) => {
    if (activeKey == null) {
      setInternalActiveKey(nextKey);
    }
    if (typeof onActiveKeyChange === "function") {
      onActiveKeyChange(nextKey);
    }
  };

  useEffect(() => {
    if (!open) return;
    const currentFiles = filesRef.current;
    if (!currentFiles.some((file) => file.key === resolvedActiveKey)) {
      setInternalActiveKey(firstFileKey);
    }
  }, [open, fileKeys, firstFileKey, resolvedActiveKey]);

  useEffect(() => {
    if (!open) {
      revokePreviewUrl();
      setError(false);
      return undefined;
    }

    if (externalLoading) {
      setError(false);
      setDocumentReady(false);
      return undefined;
    }

    const file = filesRef.current.find((item) => item.key === resolvedActiveKey)
      || filesRef.current[0];

    if (!file) {
      setError(true);
      return undefined;
    }

    const hasAsyncSource = typeof file.getSrc === "function";
    const readySource = hasAsyncSource ? null : file.src;

    if (!hasAsyncSource && !readySource) {
      setError(true);
      return undefined;
    }

    if (isObjectUrl(readySource)) {
      showPreview(readySource);
      return undefined;
    }
    if (isBlob(readySource)) {
      if (blobSrcRef.current !== readySource || !previewUrlRef.current) {
        showPreview(URL.createObjectURL(readySource), { revoke: true, blob: readySource });
      } else {
        setError(false);
      }
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;

    const loadPreview = async () => {
      setError(false);
      setViewerFailed(false);

      try {
        const source = await resolveSource(file, controller.signal);
        if (cancelled || controller.signal.aborted) return;

        if (!source) {
          if (!previewUrlRef.current) setError(true);
          return;
        }

        if (isObjectUrl(source)) {
          showPreview(source);
          return;
        }

        let blob = null;
        if (isBlob(source)) {
          blob = source;
        } else if (isRemoteUrl(source)) {
          blob = await fetchPdfBlob(source, controller.signal);
        } else if (source && typeof source.arrayBuffer === "function") {
          blob = new Blob([await source.arrayBuffer()], { type: "application/pdf" });
        }

        if (cancelled || controller.signal.aborted) return;

        if (!blob || blob.size === 0) {
          if (!previewUrlRef.current) setError(true);
          return;
        }

        if (blobSrcRef.current === blob && previewUrlRef.current) {
          setError(false);
          return;
        }

        showPreview(URL.createObjectURL(blob), { revoke: true, blob });
      } catch (loadError) {
        if (cancelled || controller.signal.aborted || isCanceled(loadError)) {
          return;
        }
        if (!previewUrlRef.current) setError(true);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [
    activeFileSrc,
    externalLoading,
    fileKeys,
    open,
    resolvedActiveKey,
    revokePreviewUrl,
    showPreview,
  ]);

  useEffect(() => () => {
    if (shouldRevokeRef.current && previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  const handleDownload = async () => {
    if (!canTriggerDownload) return;
    if (typeof onDownload === "function") {
      await onDownload(activeFile, previewUrl);
      return;
    }
    if (!activeFile) return;
    downloadFromUrl(previewUrl, activeFile.fileName);
  };

  const handlePrint = () => {
    if (!canUsePreview) return;
    printFromUrl(previewUrl);
  };

  const showErrorState = viewerFailed || (error && !previewUrl);
  const showSpinner = isPending && !showErrorState;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      closable={false}
      maskClosable={maskClosable}
      width={getModalWidth(width)}
      zIndex={zIndex}
      wrapClassName={`PdfPreviewModal ${className}`.trim()}
    >
      <div className="PdfPreviewModal__Layout">
        <div className="PdfPreviewModal__Header">
          <div className="PdfPreviewModal__HeaderLeft">
            {title ? (
              <span className="PdfPreviewModal__Title">{title}</span>
            ) : (
              <div className="PdfPreviewModal__Tabs" role={showTabs ? "tablist" : undefined}>
                {showTabs && files.map((file) => {
                  const isActive = file.key === activeFile?.key;

                  return (
                    <button
                      key={file.key}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={[
                        "PdfPreviewModal__Tab",
                        isActive ? "PdfPreviewModal__Tab--active" : "",
                      ].filter(Boolean).join(" ")}
                      onClick={() => setActiveFileKey(file.key)}
                    >
                      <FileTextOutlined aria-hidden />
                      <span>{file.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="PdfPreviewModal__Actions">
            {showDownload && showLabeledDownload && (
              <Button
                type="primary"
                className="PdfPreviewModal__DownloadButton"
                icon={<DownloadOutlined aria-hidden />}
                loading={downloadLoading}
                disabled={!canTriggerDownload}
                onClick={handleDownload}
              >
                {downloadLabel}
              </Button>
            )}
            {showDownload && !showLabeledDownload && (
              <StyledTooltip title="Download" placement="bottom">
                <button
                  type="button"
                  className="PdfPreviewModal__IconButton"
                  aria-label="Download"
                  disabled={!canTriggerDownload}
                  onClick={handleDownload}
                >
                  <DownloadOutlined aria-hidden />
                </button>
              </StyledTooltip>
            )}
            {showPrint && (
              <StyledTooltip title="Print" placement="bottom">
                <button
                  type="button"
                  className="PdfPreviewModal__IconButton"
                  aria-label="Print"
                  disabled={!canUsePreview}
                  onClick={handlePrint}
                >
                  <PrinterOutlined aria-hidden />
                </button>
              </StyledTooltip>
            )}
            <StyledTooltip title="Close" placement="bottom">
              <button
                type="button"
                className="PdfPreviewModal__IconButton"
                aria-label="Close"
                onClick={onClose}
              >
                <CloseOutlined aria-hidden />
              </button>
            </StyledTooltip>
          </div>
        </div>

        {headerExtra && (
          <div className="PdfPreviewModal__HeaderExtra">
            {headerExtra}
          </div>
        )}

        <div className="PdfPreviewModal__Body">
          <div className="PdfPreviewModal__Canvas">
            {previewUrl && !showErrorState && (
              <div className="PdfPreviewModal__Page">
                <PdfPreview
                  key={previewUrl}
                  blob={previewUrl}
                  height="100%"
                  scaleType="PageWidth"
                  scrollBarBorderRadius="0"
                  handleLoadingDone={() => setDocumentReady(true)}
                  onError={() => setViewerFailed(true)}
                />
              </div>
            )}
            {showSpinner && (
              <div
                className="PdfPreviewModal__Status PdfPreviewModal__Status--overlay"
                role="status"
                aria-label="Loading preview"
              >
                <LoadingOutlined spin className="PdfPreviewModal__LoadingIcon" />
              </div>
            )}
            {showErrorState && (
              <div className="PdfPreviewModal__Status" role="status">
                <p>{errorMessage}</p>
              </div>
            )}
          </div>
          {extra}
        </div>

        {footer && (
          <div className="PdfPreviewModal__Footer">
            {footer}
          </div>
        )}
      </div>
    </Modal>
  );
}
